// src/components/coins/GiveCoinsPopup.js
//
// "🎁 Give coins" from a subject page — the Classroom's Give coins panel in a
// popup, so a teacher can reward kids in the middle of a lesson. Tap the
// students' avatars to pick some, or give to the whole class.
//
//   <GiveCoinsPopup code={educatorCode} onClose={…} preselect={[uid]} />

import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import ModalPortal from "../common/ModalPortal";
import FocusAvatar from "../avatar/FocusAvatar";
import ClassGiveCoins from "./ClassGiveCoins";
import { listRoster } from "../../utils/educatorCode";
import "./coins.css";

const GiveCoinsPopup = ({ code, onClose, preselect = [] }) => {
  const [students, setStudents] = useState(null);
  const [picked, setPicked] = useState(preselect);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    const me = getAuth().currentUser?.uid;
    listRoster(code)
      .then((rows) => {
        if (!alive) return;
        const kids = rows
          .map((r) => ({ ...r, uid: r.uid || r.id }))
          .filter((r) => r.uid && r.uid !== me && !String(r.intendedRole || "").includes("educator"))
          .sort((a, b) => `${a.firstName || ""}${a.lastName || ""}`.localeCompare(`${b.firstName || ""}${b.lastName || ""}`));
        setStudents(kids);
      })
      .catch((e) => alive && setErr(e?.message || "Couldn't load your class."));
    return () => {
      alive = false;
    };
  }, [code]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (uid) => setPicked((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));

  return (
    <ModalPortal>
      <div className="sbl-gcp-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="sbl-gcp" role="dialog" aria-modal="true" aria-label="Give coins">
          <button type="button" className="sbl-gcp-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
          <h2>🎁 Reward your class</h2>
          {err && <p className="sbl-cgc-problem">{err}</p>}
          {!students && !err && <p className="sbl-gcp-note">Loading your class…</p>}
          {students && students.length === 0 && <p className="sbl-gcp-note">No students in your class yet — share your class code <b>{code}</b> with them.</p>}
          {students && students.length > 0 && (
            <>
              <p className="sbl-gcp-note">
                Tap students to pick them ({picked.length} picked)
                {picked.length > 0 && (
                  <button type="button" className="sbl-gcp-clear" onClick={() => setPicked([])}>
                    Clear
                  </button>
                )}
              </p>
              <div className="sbl-gcp-kids">
                {students.map((s) => {
                  const on = picked.includes(s.uid);
                  return (
                    <button key={s.uid} type="button" className={`sbl-gcp-kid ${on ? "is-on" : ""}`} onClick={() => toggle(s.uid)} aria-pressed={on}>
                      <span className="sbl-gcp-tick">{on ? "✓" : ""}</span>
                      <span className="sbl-gcp-av" style={{ pointerEvents: "none" }}>
                        <FocusAvatar id={`gcp-${s.uid}`} uid={s.uid} size={58} />
                      </span>
                      <span className="sbl-gcp-name">{s.firstName || "Student"} {s.lastName ? `${String(s.lastName)[0]}.` : ""}</span>
                    </button>
                  );
                })}
              </div>
              <ClassGiveCoins key={picked.length ? "p" : "a"} students={students} selected={picked} onClear={() => setPicked([])} pickHint="above" startWho={picked.length ? "picked" : "all"} />
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default GiveCoinsPopup;
